import React, { useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Maximize2, X, LocateFixed } from "lucide-react";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/components/FileUpload";
import { useApp } from "@/context/AppContext";
import { liveTitle } from "@/lib/liveTitle";

// Fetch nearby LIVES + CHURCHES around a GPS point → unified map points, so a single
// map can show both and let the user filter between "Directs" and "Églises".
export const useNearbyMapPoints = (coords) => {
  const [points, setPoints] = useState([]);
  const { t } = useApp();
  const tRef = useRef(t);
  tRef.current = t;
  const lat = coords?.lat;
  const lon = coords?.lon;
  const radiusKm = coords?.radiusKm;
  useEffect(() => {
    if (lat == null || lon == null) { setPoints([]); return; }
    let cancelled = false;
    const churchParams = new URLSearchParams({ lat: String(lat), lon: String(lon), radius_km: String(radiusKm || 25), limit: "50" });
    Promise.all([
      api.get("/live", { params: { status: "live", lat, lon } }).catch(() => ({ data: { lives: [] } })),
      api.get(`/churches?${churchParams.toString()}`).catch(() => ({ data: { churches: [] } })),
    ]).then(([lv, ch]) => {
      if (cancelled) return;
      const livePts = (lv.data.lives || [])
        .filter((l) => l.church_lat != null && l.church_lon != null)
        .map((l) => ({
          lat: l.church_lat, lon: l.church_lon, type: "live", id: l.id,
          label: liveTitle(l, tRef.current), sub: l.church_name || l.host?.name, viewers: l.viewers ?? 0,
          image: resolveMediaUrl(l.thumbnail_url || l.embed?.thumbnail || l.host?.avatar_url),
        }));
      const churchPts = (ch.data.churches || [])
        .filter((c) => c.lat != null && c.lon != null)
        .map((c) => ({
          lat: c.lat, lon: c.lon, type: "church", id: c.id,
          label: c.name, sub: [c.city, c.country].filter(Boolean).join(" · "),
          image: resolveMediaUrl(c.cover_url || c.logo_url),
        }));
      setPoints([...livePts, ...churchPts]);
    });
    return () => { cancelled = true; };
  }, [lat, lon, radiusKm]);
  return points;
};

const esc = (s) => String(s == null ? "" : s).replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c]));

const liveIcon = (viewers) => L.divIcon({
  className: "",
  html: `<div style="position:relative;display:grid;place-items:center;width:32px;height:32px">
    <span class="animate-ping" style="position:absolute;width:26px;height:26px;border-radius:9999px;background:#e11d48;opacity:.5"></span>
    <span style="position:relative;width:20px;height:20px;border-radius:9999px;background:#e11d48;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4)"></span>
    <span style="position:absolute;top:-11px;display:flex;align-items:center;gap:3px;white-space:nowrap;background:#e11d48;color:#fff;font:700 9px/1 system-ui,sans-serif;padding:2px 5px;border-radius:9999px;box-shadow:0 1px 3px rgba(0,0,0,.35)"><span style="width:5px;height:5px;border-radius:9999px;background:#fff"></span>${Number(viewers) || 0}</span>
  </div>`,
  iconSize: [32, 32], iconAnchor: [16, 16], popupAnchor: [0, -18],
});

const churchIcon = () => L.divIcon({
  className: "",
  html: `<div style="display:grid;place-items:center;width:26px;height:26px;border-radius:9999px;background:#2D5A4C;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35)"><span style="width:8px;height:8px;border-radius:9999px;background:#fff;display:block"></span></div>`,
  iconSize: [26, 26], iconAnchor: [13, 13], popupAnchor: [0, -14],
});

// Rich popup card: thumbnail + title + subtitle + a Join / View button (data-join).
const popupHtml = (p, t) => {
  const isLive = p.type === "live";
  const btnColor = isLive ? "#e11d48" : "#2D5A4C";
  const btnLabel = isLive ? t("map.join_live", "Rejoindre le direct") : t("map.view_church", "Voir l'église");
  const sub = isLive ? [p.sub, `${Number(p.viewers) || 0} spectateur${(Number(p.viewers) || 0) > 1 ? "s" : ""}`].filter(Boolean).join(" · ") : p.sub;
  return `<div style="width:196px">
    ${p.image ? `<div style="position:relative"><img src="${esc(p.image)}" style="width:100%;height:104px;object-fit:cover;border-radius:8px;background:#000" onerror="this.style.display='none'"/>${isLive ? `<span style="position:absolute;top:6px;left:6px;background:#e11d48;color:#fff;font:800 9px/1 system-ui;letter-spacing:.04em;padding:3px 6px;border-radius:6px">${esc(t("map.live_badge", "● EN DIRECT"))}</span>` : ""}</div>` : ""}
    <div style="font-weight:800;margin-top:6px;line-height:1.2">${esc(p.label)}</div>
    ${sub ? `<div style="opacity:.7;font-size:12px;margin-top:2px">${esc(sub)}</div>` : ""}
    <button data-join type="button" style="display:flex;align-items:center;justify-content:center;gap:6px;width:100%;margin-top:9px;padding:8px;border:none;border-radius:9999px;background:${btnColor};color:#fff;font:800 12px system-ui;cursor:pointer">${btnLabel}</button>
  </div>`;
};

// Core Leaflet map: user + nearby lives (animated + viewers) and churches, with a
// Tous / Directs / Églises filter, rich click popups, and expand/close controls.
const MiniMapCore = ({ center, points = [], height = 220, onPointClick, showExpand, onExpand, onClose }) => {
  const { t } = useApp();
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const liveLayerRef = useRef(null);
  const churchLayerRef = useRef(null);
  const clickRef = useRef(onPointClick);
  clickRef.current = onPointClick;

  const hasLive = points.some((p) => p.type === "live");
  const hasChurch = points.some((p) => p.type === "church");
  const [filter, setFilter] = useState("all");

  const sig = JSON.stringify([center?.lat, center?.lon, points.map((p) => [p.lat, p.lon, p.type, p.viewers])]);

  useEffect(() => {
    if (!elRef.current || center?.lat == null) return;
    const map = L.map(elRef.current, { zoomControl: false, attributionControl: true, scrollWheelZoom: false });
    mapRef.current = map;
    L.control.zoom({ position: onClose ? "bottomleft" : "topleft" }).addTo(map);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" }).addTo(map);

    const userIcon = L.divIcon({ className: "", html: `<div style="width:16px;height:16px;border-radius:9999px;background:#2563eb;border:3px solid #fff;box-shadow:0 0 0 3px rgba(37,99,235,.35)"></div>`, iconSize: [16, 16], iconAnchor: [8, 8] });
    L.marker([center.lat, center.lon], { icon: userIcon, interactive: false }).addTo(map);

    const liveLayer = L.layerGroup();
    const churchLayer = L.layerGroup();
    const bounds = [[center.lat, center.lon]];
    points.forEach((p) => {
      if (p.lat == null || p.lon == null) return;
      const m = L.marker([p.lat, p.lon], { icon: p.type === "live" ? liveIcon(p.viewers) : churchIcon(), zIndexOffset: p.type === "live" ? 1000 : 0 });
      m.bindPopup(popupHtml(p, t), { maxWidth: 220, minWidth: 196, closeButton: true });
      m.on("popupopen", (e) => {
        const btn = e.popup.getElement()?.querySelector("[data-join]");
        if (btn) btn.onclick = (ev) => { ev.preventDefault(); clickRef.current?.(p); };
      });
      m.addTo(p.type === "live" ? liveLayer : churchLayer);
      bounds.push([p.lat, p.lon]);
    });
    liveLayer.addTo(map);
    churchLayer.addTo(map);
    liveLayerRef.current = liveLayer;
    churchLayerRef.current = churchLayer;

    if (bounds.length > 1) map.fitBounds(bounds, { padding: [34, 34], maxZoom: 14 });
    else map.setView([center.lat, center.lon], 13);
    const tid = setTimeout(() => map.invalidateSize(), 130);

    return () => { clearTimeout(tid); map.remove(); mapRef.current = null; liveLayerRef.current = null; churchLayerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, height]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const showLive = filter === "all" || filter === "live";
    const showChurch = filter === "all" || filter === "church";
    if (liveLayerRef.current) { if (showLive) liveLayerRef.current.addTo(map); else map.removeLayer(liveLayerRef.current); }
    if (churchLayerRef.current) { if (showChurch) churchLayerRef.current.addTo(map); else map.removeLayer(churchLayerRef.current); }
  }, [filter, sig]);

  const tabs = [["all", "Tous"], ["live", "Directs"], ["church", "Églises"]];
  const recenter = () => { const map = mapRef.current; if (map && center?.lat != null) map.flyTo([center.lat, center.lon], 14, { duration: 0.6 }); };
  const containerStyle = { height: typeof height === "number" ? height : height, width: "100%", borderRadius: onClose ? 0 : 16, overflow: "hidden", zIndex: 0 };
  return (
    <div style={{ position: "relative", height: typeof height === "number" ? height : "100%", width: "100%" }}>
      <div ref={elRef} style={containerStyle} data-testid="nearby-mini-map" />

      {hasLive && hasChurch && (
        <div className="absolute top-2 right-2 flex rounded-full overflow-hidden shadow-md" style={{ zIndex: 500, background: "var(--yalola-surface)", border: "1px solid var(--yalola-border)" }} data-testid="map-filter">
          {tabs.map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} className="px-2.5 py-1 text-[11px] font-bold transition-colors" style={filter === k ? { background: "var(--yalola-primary)", color: "#fff" } : { color: "var(--yalola-text-muted)", background: "transparent" }} data-testid={`map-filter-${k}`}>{lbl}</button>
          ))}
        </div>
      )}

      {showExpand && (
        <div className="absolute bottom-2 right-2 flex flex-col gap-2" style={{ zIndex: 500 }}>
          <button type="button" onClick={recenter} title={t("map.locate_me", "Me localiser")} aria-label={t("map.locate_me", "Me localiser")} className="h-9 w-9 grid place-items-center rounded-full shadow-md transition-transform hover:scale-105" style={{ background: "var(--yalola-surface)", border: "1px solid var(--yalola-border)", color: "var(--yalola-primary)" }} data-testid="map-recenter-btn">
            <LocateFixed size={16} />
          </button>
          <button type="button" onClick={onExpand} title={t("map.expand", "Agrandir la carte")} aria-label={t("map.expand", "Agrandir la carte")} className="h-9 w-9 grid place-items-center rounded-full shadow-md transition-transform hover:scale-105" style={{ background: "var(--yalola-surface)", border: "1px solid var(--yalola-border)", color: "var(--yalola-primary)" }} data-testid="map-expand-btn">
            <Maximize2 size={16} />
          </button>
        </div>
      )}

      {onClose && (
        <button type="button" onClick={recenter} title={t("map.locate_me", "Me localiser")} aria-label={t("map.locate_me", "Me localiser")} className="absolute bottom-3 right-3 h-11 w-11 grid place-items-center rounded-full shadow-lg transition-transform hover:scale-105" style={{ zIndex: 600, background: "var(--yalola-surface)", border: "1px solid var(--yalola-border)", color: "var(--yalola-primary)" }} data-testid="map-recenter-btn">
          <LocateFixed size={18} />
        </button>
      )}

      {onClose && (
        <button type="button" onClick={onClose} title={t("map.close", "Fermer")} aria-label={t("map.close", "Fermer")} className="absolute top-3 left-3 h-10 w-10 grid place-items-center rounded-full shadow-lg transition-transform hover:scale-105" style={{ zIndex: 600, background: "var(--yalola-surface)", border: "1px solid var(--yalola-border)", color: "var(--yalola-text)" }} data-testid="map-close-btn">
          <X size={18} />
        </button>
      )}
    </div>
  );
};

// Small interactive OpenStreetMap with an "expand to fullscreen" affordance.
export const NearbyMiniMap = ({ center, points = [], height = 220, onPointClick }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <MiniMapCore center={center} points={points} height={height} onPointClick={onPointClick} showExpand onExpand={() => setExpanded(true)} />
      {expanded && (
        <div className="fixed inset-0 flex p-2 sm:p-4" style={{ zIndex: 2000, background: "rgba(0,0,0,0.78)", backdropFilter: "blur(2px)" }} data-testid="map-fullscreen" onClick={(e) => { if (e.target === e.currentTarget) setExpanded(false); }}>
          <div className="relative m-auto w-full h-full rounded-2xl overflow-hidden" style={{ background: "var(--yalola-surface)", maxWidth: 1100 }}>
            <MiniMapCore center={center} points={points} height="100%" onPointClick={(p) => { setExpanded(false); onPointClick?.(p); }} onClose={() => setExpanded(false)} />
          </div>
        </div>
      )}
    </>
  );
};

export default NearbyMiniMap;
